// ============================================================
//  src/pages/agent/AgentLoginPage.js
//  Standalone login for human agents -- no platform/subscriber
//  account needed. They authenticate with { bId, email, password }
//  against the bot_agents record the bot owner created for them.
// ============================================================
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { agentLogin } from '../../api/client';
import { getError } from '../../utils/helpers';
import '../auth/auth.css';

export default function AgentLoginPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const invitedBId = (params.get('bId') || '').trim();

  const [form, setForm]       = useState({ bId: invitedBId, email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await agentLogin(form.bId.trim(), form.email.trim(), form.password);
      const { token, agent } = res.data.data;
      localStorage.setItem('agentAuthToken', token);
      localStorage.setItem('agentInfo', JSON.stringify(agent));
      toast.success(`Welcome, ${agent.aName}`);
      nav('/agent/console', { replace: true });
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-main">
          <div className="auth-brand">
            <span className="auth-brand-dot">C</span>
            <span>CommandKit</span>
          </div>
          <div>
            <div className="auth-headline">Agent<br /><em>Support Portal.</em></div>
            <p className="auth-sub">
              Sign in with the credentials your bot owner gave you to reply to
              customers assigned to you and hand conversations back to the bot
              when you're done.
            </p>
          </div>
          <div className="auth-footer-badge">
            <i className="fas fa-lock" /> 256-bit SSL
          </div>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-shell">
          <div className="auth-mobile-brand">
            <span className="auth-brand-dot">C</span> CommandKit
          </div>

          <div className="auth-hdr">
            <h2>Agent sign in</h2>
            <p>
              {invitedBId
                ? 'Enter the email and password your bot owner gave you.'
                : 'Enter the Bot ID, email and password your bot owner gave you.'}
            </p>
          </div>

          {error && (
            <div className="auth-alert auth-alert-error">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          <form onSubmit={submit} noValidate>
            <div className="auth-field">
              <label>Bot ID</label>
              <div className="auth-field-wrap">
                <i className="fas fa-robot fi" />
                <input
                  type="text"
                  value={form.bId}
                  onChange={set('bId')}
                  placeholder="e.g. 6EXKTdtZfj"
                  autoComplete="off"
                  readOnly={!!invitedBId}
                  required
                />
              </div>
              {invitedBId && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted, #6b7280)', marginTop: 4 }}>
                  <i className="fas fa-circle-check" /> Pre-filled from your invite link
                </div>
              )}
            </div>

            <div className="auth-field">
              <label>Email</label>
              <div className="auth-field-wrap">
                <i className="fas fa-user fi" />
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="agent@example.com"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="auth-field">
              <label>Password</label>
              <div className="auth-field-wrap">
                <i className="fas fa-lock fi" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={set('password')}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
                <button type="button" className="pw-toggle" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}>
                  <i className={`fas ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
            </div>

            <button type="submit" className="btn-auth" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Signing in…</>
                : <><i className="fas fa-arrow-right-to-bracket" /> Sign in</>
              }
            </button>
          </form>

          <div className="auth-bottom">
            Not an agent? <a href="/login">Go to the main login</a>
          </div>
        </div>
      </div>
    </div>
  );
}